import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ArrowRightIcon from '../ArrowRightIcon';

const FundsTable = ({ selectedFrequency }) => {
  const navigate = useNavigate();
  const breakdown = useSelector((s) => s?.adminReducer?.dashboardFundBreakdown);

  const rows = Array.isArray(breakdown?.list) ? breakdown.list : [];
  const funds = rows.map((r) => ({
    username: r?.username || '-',
    email: r?.email || '-',
    fundraiser: r?.fundraiser || '-',
    funds: typeof r?.funds === 'number'
      ? (r.funds / 100).toFixed(2)
      : '-',
    nextPayout: r?.nextPaymentDate === 'Available Now'
      ? 'Available Now'
      : r?.nextPaymentDate
      ? new Date(r.nextPaymentDate).toLocaleDateString()
      : '-',
  }));

  const totalPendingFormatted = typeof breakdown?.totalPending === 'number'
    ? `$${(breakdown.totalPending / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '$0.00';

  const numFundraisers = breakdown?.numFundraisers || 0;

  const nextPaymentFormatted = breakdown?.nextPaymentDate === 'Available Now'
    ? 'Available Now'
    : breakdown?.nextPaymentDate
    ? new Date(breakdown.nextPaymentDate).toLocaleDateString()
    : 'Available Now';

  const avgPaymentFormatted = typeof breakdown?.avgPayment === 'number'
    ? `$${(breakdown.avgPayment / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '$0.00';

  return (
    <div className="summary w-full p-4 min-h-[470px] lg:h-[470px] flex flex-col bg-white rounded-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h2 className="text-[20px] font-poppins font-[600] text-[#303030] leading-none">Funds to be Disbursed (30 Days)</h2>
        <button onClick={()=> navigate('/admin/total-income?tab=to-be-disbursed')} className="text-[#00ADEF] text-[16px] font-poppins font-[500] border border-[#00ADEF] rounded-[6px] px-4 py-2 transition hover:bg-[#E6F7FB] flex items-center">
          View All
          <ArrowRightIcon className="ml-2" />
        </button>
      </div>

      {/* 3 Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-[#F8F9FA] p-3 rounded-lg flex flex-col">
          <span className="text-[11px] font-poppins text-[#767676] uppercase font-[500] whitespace-nowrap">Total Pending</span>
          <span className="text-[16px] xl:text-[18px] font-poppins font-[700] text-[#303030] mt-1 whitespace-nowrap">{totalPendingFormatted}</span>
        </div>
        <div className="bg-[#F8F9FA] p-3 rounded-lg flex flex-col">
          <span className="text-[11px] font-poppins text-[#767676] uppercase font-[500] whitespace-nowrap">Fundraisers</span>
          <span className="text-[16px] xl:text-[18px] font-poppins font-[700] text-[#303030] mt-1 whitespace-nowrap">{numFundraisers}</span>
        </div>
        <div className="bg-[#F8F9FA] p-3 rounded-lg flex flex-col">
          <span className="text-[11px] font-poppins text-[#767676] uppercase font-[500] whitespace-nowrap">Avg Payment</span>
          <span className="text-[16px] xl:text-[18px] font-poppins font-[700] text-[#303030] mt-1 whitespace-nowrap">{avgPaymentFormatted}</span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto pr-1">
        {funds.length > 0 ? (
          <table className="w-full min-w-[700px] table-fixed border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-gray-200">
                <th className="w-[18%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Username</th>
                <th className="w-[26%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Email</th>
                <th className="w-[24%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Fundraiser</th>
                <th className="w-[18%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Next Payout</th>
                <th className="w-[14%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Funds</th>
              </tr>
            </thead>
            <tbody>
              {funds.map((fund, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={fund.username}>{fund.username}</td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={fund.email}>{fund.email}</td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={fund.fundraiser}>{fund.fundraiser}</td>
                  <td className="py-3 px-2 text-[13px] font-poppins align-middle">
                    {fund.nextPayout === 'Available Now' ? (
                      <span className="px-2 py-0.5 rounded-[4px] text-[11px] font-[500] uppercase bg-green-100 text-green-700">
                        Available Now
                      </span>
                    ) : (
                      <span className="text-[#767676] font-[500]">
                        {fund.nextPayout}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-gray-900 font-[600] align-middle">${fund.funds}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#767676] text-[14px] py-12">No data</div>
        )}
      </div>
    </div>
  );
};

export default FundsTable; 
